import {App, computed, reactive, watch} from 'vue';
import { ComputedRef, UnwrapNestedRefs } from "@vue/reactivity";
import EventBusService from "@/services/EventBusService";
import { GlobalStoreNotification } from "@/data/eventbus";

const localStorageKey = 'buggyfit-global-storage';

// the store in its current implementation only supports a shallow object!!! no nesting
export interface GlobalStoreData {
    loggedIn: boolean,
    userRole: number,
    authFailed: boolean,
    voucherCode: string|null,
    test: number,
}

export type GlobalStoreDataKey = keyof GlobalStoreData;

const globalStoreDataKeys: GlobalStoreDataKey[] = ['loggedIn', 'userRole', 'authFailed', 'voucherCode', 'test'];


interface GlobalStoreInterfaceInternal {
    data: UnwrapNestedRefs<GlobalStoreData>,
}

interface GlobalStoreInterfaceExternal {
    data: {
        loggedIn: ComputedRef<boolean>,
        userRole: ComputedRef<number>,
        authFailed: ComputedRef<boolean>,
        voucherCode: ComputedRef<string|null>,
        test: ComputedRef<number>,
    },
    set: (keyOrMap: GlobalStoreDataKey|Partial<GlobalStoreData>, value?: any) => void,
    unauthenticatedResponseReceived: () => void,
}

export type GlobalStoreInterface = GlobalStoreInterfaceExternal;

const persistedKeys: Array<keyof GlobalStoreData> = ['loggedIn', 'test', 'voucherCode'];

const defaultValues: GlobalStoreData = {
    loggedIn: false,
    userRole: -1, // === Customer
    authFailed: false,
    voucherCode: null,
    test: 23,
};




const isMaster: boolean = (() => {
    const masterExists = typeof (window as any).buggyFitGlobalStoreMaster !== 'undefined';
    if( !masterExists ) {
        (window as any).buggyFitGlobalStoreMaster = {
            data: {
                ...defaultValues,
                ...loadFromLocalStorage(),
            },
        };
    }
    return !masterExists;
})();


const globalStore = {
    data: reactive<GlobalStoreData>(jsonCopy((window as any).buggyFitGlobalStoreMaster.data)),
} as GlobalStoreInterfaceInternal;


if( isMaster ) {
    for( const key of globalStoreDataKeys ) {
        watch(
            () => globalStore.data[key],
            (value, oldValue) => {
                saveToLocalStorage(globalStore.data);
                EventBusService.$emit('globalStore:notifySlaves', { key, value });
            }
        );
    }

    EventBusService.$on('globalStore:notifyMaster', (event: GlobalStoreNotification) => {
        (window as any).buggyFitGlobalStoreMaster.data[event.key] = event.value;
        globalStore.data[event.key] = event.value as never; // this will trigger emit of "globalStore:notifySlaves" event
    });
}
else {
    EventBusService.$on('globalStore:notifySlaves', (event: GlobalStoreNotification) => {
        globalStore.data[event.key] = event.value as never;
    });
}


/**
 * Some borderline hacky typescript stuff happening with the type assertions.
 */
function set(keyOrMap: GlobalStoreDataKey|Partial<GlobalStoreData>, value?: any): void {
    if( typeof keyOrMap === 'string' ) {
        keyOrMap = {
            [keyOrMap]: value,
        } as Partial<GlobalStoreData>;
    }

    keyOrMap = keyOrMap as Partial<GlobalStoreData>;

    if( isMaster ) {
        Object.entries(keyOrMap).map(([key, value]) => (window as any).buggyFitGlobalStoreMaster.data[key] = value);
    }
    Object.entries(keyOrMap).map(([key, value]) => globalStore.data[key as GlobalStoreDataKey] = value as never); // silence TS compiler with "never"

    if( !isMaster ) {
        Object.entries(keyOrMap).map(([key, value]) => EventBusService.$emit('globalStore:notifyMaster', { key: key as GlobalStoreDataKey, value }));
    }
}

const storeExport: GlobalStoreInterface = {
    data: {
        loggedIn:   computed(() => globalStore.data.loggedIn),
        userRole:   computed(() => globalStore.data.userRole),
        authFailed: computed(() => globalStore.data.authFailed),
        voucherCode: computed(() => globalStore.data.voucherCode),
        test:       computed(() => globalStore.data.test),
    },
    set,
    unauthenticatedResponseReceived() {
        set({
            loggedIn: false,
            userRole: -1, // === Customer
            authFailed: true,
        });
    },
};

export default storeExport;


export function initGlobalStore(app: App) : App {
    app.config.globalProperties.$store = storeExport;

    return app;
}


function filterPersistedKeys(obj: Partial<GlobalStoreData>) : Partial<GlobalStoreData> {
    const filtered: Partial<GlobalStoreData> = {};
    for( const key of persistedKeys ) {
        if( typeof obj[key] !== 'undefined' ) {
            filtered[key] = obj[key] as any;
        }
    }
    return filtered;
}


function loadFromLocalStorage() : Partial<GlobalStoreData> {
    try {
        const dataRaw = window.localStorage.getItem(localStorageKey) || '{}';
        console.warn('restoring global store from local storage: ', dataRaw);
        const data = JSON.parse(dataRaw) as Partial<GlobalStoreData>;
        return filterPersistedKeys(data);
    }
    catch( e ) {
        console.warn('could not restore global store from local storage', e);
        throw e;
        return {};
    }
}

function saveToLocalStorage(store: GlobalStoreData) : void {
    try {
        console.warn('persisting global store to local storage');
        window.localStorage.setItem(localStorageKey, JSON.stringify(filterPersistedKeys(store)));
    }
    catch( e ) {
        console.warn('could not persist global store to local storage', e);
    }
}

function jsonCopy(obj: any) {
    return JSON.parse(JSON.stringify(obj));
}
